import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, ArrowUpRight, Sparkles, TrendingUp, Megaphone, Palette, DollarSign, Settings } from "lucide-react";
import { api, openBooking, openExternal } from "../lib/site";
import PageHero from "../components/site/PageHero";
import { Reveal } from "../components/site/Reveal";
import { Overline, PrimaryButton } from "../components/site/Primitives";

const EASE = [0.16, 1, 0.3, 1];

const PILLARS = [
  { icon: Sparkles, label: "AI Masterclass" },
  { icon: TrendingUp, label: "Business Coaching" },
  { icon: Megaphone, label: "Marketing" },
  { icon: Palette, label: "Branding" },
  { icon: DollarSign, label: "Pricing" },
  { icon: Settings, label: "Systems & Automation" },
];

export default function Blueprint() {
  const [programs, setPrograms] = useState([]);
  useEffect(() => {
    api.get("/programs?track=blueprint").then((r) => setPrograms(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="blueprint-page">
      <PageHero
        overline="Nail Boss Blueprint"
        title="Turn your talent into a thriving business."
        subtitle="Business education for nail professionals — AI, coaching, marketing, branding, pricing, systems, automation, and content creation, all in one place."
      >
        <PrimaryButton onClick={openBooking} data-testid="blueprint-register-btn">Register Now</PrimaryButton>
      </PageHero>

      {/* Pillars */}
      <section className="bg-brand-cream py-20 md:py-28" data-testid="blueprint-pillars">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {PILLARS.map((p, i) => (
              <Reveal key={p.label} delay={i * 0.05} className="bg-brand-blush rounded-2xl p-6 text-center flex flex-col items-center">
                <span className="h-12 w-12 rounded-full bg-white flex items-center justify-center text-brand-rose mb-4"><p.icon size={22} strokeWidth={1.6} /></span>
                <span className="text-sm font-medium text-brand-ink">{p.label}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Offers */}
      <section className="bg-brand-blush py-24 md:py-32" data-testid="blueprint-offers">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <Reveal className="mb-14 max-w-2xl"><Overline className="mb-5">Ways to enroll</Overline><h2 className="font-serif font-light text-brand-ink text-4xl sm:text-5xl lg:text-6xl tracking-tight">Choose your path to growth.</h2></Reveal>
          <div className="grid gap-6 md:grid-cols-2">
            {programs.map((p, i) => (
              <motion.div key={p.id} data-testid={`blueprint-card-${p.id}`} initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.8, ease: EASE, delay: i * 0.08 }} className="bg-white rounded-3xl border border-brand-ink/10 overflow-hidden flex flex-col">
                <div className="relative h-52 overflow-hidden group">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
                </div>
                <div className="p-8 md:p-9 flex flex-col flex-1">
                  <span className="text-[11px] uppercase tracking-[0.24em] text-brand-rose">{p.subtitle}</span>
                  <h3 className="font-serif text-3xl text-brand-ink tracking-tight mt-2">{p.title}</h3>
                  <p className="text-brand-ink/70 font-light mt-3 leading-relaxed">{p.description}</p>
                  <ul className="mt-5 space-y-2">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-center gap-2.5 text-sm text-brand-ink/80"><Check size={15} className="text-brand-gold shrink-0" />{f}</li>
                    ))}
                  </ul>
                  <div className="mt-auto pt-7 flex items-center justify-between">
                    <span className="font-serif text-brand-rose text-3xl">{p.price}</span>
                    <button onClick={() => openExternal(p.checkout_url)} data-testid={`blueprint-enroll-${p.id}`} className="flex items-center gap-1.5 text-sm text-brand-ink group/btn"><span className="border-b border-brand-gold pb-0.5">Register</span><ArrowUpRight size={16} className="text-brand-gold transition-transform duration-300 group-hover/btn:-translate-y-0.5 group-hover/btn:translate-x-0.5" /></button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
